import { BodyOf, Messages } from "../../../core/api";
import {
  ContractTypeId,
  PatientDocumentTypeId,
  PatientTaskTemplateId,
  PayrollCodeId,
  ServiceCodeId,
} from "../../../shared/schema/schema";

export type BulkScheduleTasksBody = BodyOf<"post", "./patient_tasks/bulk_schedule">;

export type BulkScheduleServiceCode = Messages["ServiceCodeWithPayrollCodes"];

export type BulkSchedulePatientContract = Messages["PatientContractBasicData"];

export type BulkScheduleCertificationPeriod = Messages["PatientCertificationPeriod"];

export type BulkScheduleCaregiver = Messages["CaregiverSearchItem"];

/**
 * The endpoints below are served by the legacy router and are not part of the
 * generated OpenAPI schema, so their shapes are declared here (only the fields
 * the bulk schedule modal reads).
 */
export type BulkScheduleTaskTemplate = {
  id: PatientTaskTemplateId;
  title: string;
  durationMinutes: number | null;
  documents: PatientDocumentTypeId[];
  hasPlanOfCare: boolean;
};

export type BulkSchedulePayrollCode = {
  id: PayrollCodeId;
  displayId: string;
  isActive: boolean;
};

export type BulkScheduleDocumentType = {
  id: PatientDocumentTypeId;
  title: string;
  type: string;
  versions: { isPublished: boolean }[];
};

export type BulkScheduleAgencySettings = {
  requireCms485CertificationPeriod?: boolean;
};

export type BulkScheduleContractTypeBillingRates = {
  contractTypeId: ContractTypeId;
  billingRates: { serviceCode: ServiceCodeId }[];
};

/**
 * Document types that must be tied to a certification period. Mirrors
 * `isCms485Selected` in the AngularJS new-task modal and the server check.
 */
export const DOCUMENT_TYPES_REQUIRING_CERTIFICATION_PERIOD = ["CMS485", "FC_CMS485"];

/** Scheduling window and gap used by the server, mirrored here for the day estimate. */
export const BULK_SCHEDULE_DAY_START_HOUR = 9;
export const BULK_SCHEDULE_DAY_END_HOUR = 18;
export const BULK_SCHEDULE_GAP_MINUTES = 15;
/** Server default when the template has no duration. */
export const BULK_SCHEDULE_DEFAULT_TASK_MINUTES = 45;

export const NO_PAYROLL_CODE = "NO_PAYROLL_CODE";

/**
 * How many days the caregiver needs for `taskCount` tasks, packed back to back
 * with the gap into the 09:00-18:00 window. Only an estimate: the server also
 * skips the caregiver's existing tasks.
 */
export function estimateBulkScheduleDays(taskCount: number, durationMinutes: number): number {
  const windowMinutes = (BULK_SCHEDULE_DAY_END_HOUR - BULK_SCHEDULE_DAY_START_HOUR) * 60;
  const perDay = Math.floor(
    (windowMinutes + BULK_SCHEDULE_GAP_MINUTES) / (durationMinutes + BULK_SCHEDULE_GAP_MINUTES)
  );

  if (perDay <= 0) {
    return taskCount;
  }

  return Math.ceil(taskCount / perDay);
}
